import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { formatUsdShort } from "@/lib/payments/currency";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Check, CreditCard, Hourglass, MapPin, MessageCircle } from "lucide-react";
import { StepIndicator } from "@/components/ds/step-indicator";
import { HelpCard } from "@/components/booking/help-card";
import { useHeaderEndSlot } from "@/components/public/header-slot";
import { BOOKING_STEPS } from "@/lib/booking-steps";
import { supabase } from "@/integrations/supabase/client";
import { getMyBooking, submitBookingReceipt } from "@/lib/booking.functions";
import { getPublicSiteSettings, listPaymentInstructions } from "@/lib/public.functions";
import { renderPaymentInstructions } from "@/lib/markdown";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { bookingPaymentRegionFor, instructionsRegionForPaymentMethod, paymentMethodForRegion, paymentMethodLabel, type SessionMode } from "@/lib/booking-payment";
import { BookingPaymentPanel, type BookingPaymentRegion } from "@/components/payments/booking-payment-panel";
import { buildBookingConfirmMessage } from "@/lib/booking-whatsapp";
import { toWhatsAppLink } from "@/lib/whatsapp-link";

export const bookingQ = (id: string) =>
  queryOptions({ queryKey: ["me", "booking", id], queryFn: () => getMyBooking({ data: { id } }) });
export const payQ = () => queryOptions({ queryKey: ["public", "payment_instructions"], queryFn: () => listPaymentInstructions() });
export const settingsQ = () => queryOptions({ queryKey: ["public", "site_settings"], queryFn: () => getPublicSiteSettings() });

export const Route = createFileRoute("/_public/book/confirm/$id")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/auth", search: { redirect: location.href } });
    }
  },
  loader: async ({ params, context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(bookingQ(params.id)),
      context.queryClient.ensureQueryData(payQ()),
      context.queryClient.ensureQueryData(settingsQ()),
    ]);
  },
  head: () => ({
    meta: [
      { title: "تأكيد الحجز — د. سارة النحّاس" },
      { name: "robots", content: "noindex" },
    ],
  }),
  notFoundComponent: RouteNotFound,
  errorComponent: RouteError,
  component: ConfirmPage,
});

function ConfirmPage() {
  const { id } = Route.useParams();
  const { data: bundle } = useSuspenseQuery(bookingQ(id));
  const { data: pays } = useSuspenseQuery(payQ());
  const { data: settings } = useSuspenseQuery(settingsQ());
  const upload = useServerFn(submitBookingReceipt);
  const qc = useQueryClient();
  const [paymentRegion, setPaymentRegion] = useState<BookingPaymentRegion | null>(null);

  const b = bundle.booking;
  const svc = bundle.service;
  const status = b.payment_status;
  const rejection = b.receipt_rejection_reason;
  const instructionsRegion = instructionsRegionForPaymentMethod(b.payment_method);
  const payInstruction = (pays ?? []).find((p: any) => p.region_slug === instructionsRegion) ?? null;

  // The page branches on how the booking is meant to be paid, never on
  // payment_status alone — a clinic booking is "unpaid" by design.
  const mode: "at_clinic" | "receipt" | "card" =
    b.payment_plan === "pay_at_clinic"
      ? "at_clinic"
      : b.payment_method === "paddle_card"
        ? "card"
        : "receipt";

  // No time yet → the request is still waiting for the WhatsApp confirmation.
  const awaitingTime = !b.scheduled_at;
  const needsReceipt =
    mode === "receipt" &&
    (status === "unpaid" || (status === "receipt_uploaded" && !!rejection));
  const clinicAddress = settings.clinic_address_ar || "أبو رمانة، دمشق";
  const paymentLabel = paymentMethodLabel(b.payment_method);

  const waHref = toWhatsAppLink(
    settings.clinic_whatsapp,
    buildBookingConfirmMessage({
      bookingRef: b.id.slice(0, 8),
      fullName: bundle.clientName || "—",
      serviceTitle: svc?.title_ar ?? "استشارة",
      priceUsd: svc?.price_usd ?? "",
      sessionMode: (b.session_mode as SessionMode) ?? "online",
      clinicAddress,
      paymentLabel,
      preferredTimes: (b as any).preferred_times,
    }),
  );

  const receiptWaHref = toWhatsAppLink(
    settings.clinic_whatsapp,
    `إيصال دفع للحجز ${b.id.slice(0, 8)}`,
  );

  async function uploadFile(file: File) {
    const { data: u } = await supabase.auth.getUser();
    const uid = u.user?.id;
    if (!uid) throw new Error("انتهت الجلسة — تسجيل الدخول من جديد مطلوب.");
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${uid}/${id}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("payment-receipts")
      .upload(path, file, { upsert: false, contentType: file.type });
    if (upErr) throw upErr;
    const region = paymentRegion ?? bookingPaymentRegionFor(b.payment_method);
    await upload({ data: { id, receiptPath: path, paymentMethod: paymentMethodForRegion(region) as "shamcash" | "paypal" } });
    toast.success("وصلنا الإيصال.");
    qc.invalidateQueries({ queryKey: ["me", "booking", id] });
    qc.invalidateQueries({ queryKey: ["me", "bookings"] });
  }

  useHeaderEndSlot(
    <Link to="/portal" className="inline-flex min-h-12 items-center px-2 font-display text-[15px] font-bold text-brown-500">
      لوحتي ←
    </Link>,
    "confirm",
  );

  const isPackage = svc?.slug === "monthly-package-4";
  const selectionLine = `${b.session_mode === "in_person" ? "استشارة بالعيادة" : "استشارة أونلاين"} · ${isPackage ? "4 جلسات" : "جلسة واحدة"}`;
  const ref = b.id.slice(0, 8);
  const waiting = mode === "receipt" && status === "receipt_uploaded" && !rejection;
  const showIndicator = (needsReceipt || (mode === "at_clinic" && awaitingTime)) && !waiting;
  const helpWa = toWhatsAppLink(settings.clinic_whatsapp, `سؤال عن الحجز ${ref}`);
  const primaryBtn =
    `${btn.primary} min-h-14 w-full`;
  const card = "rounded-ds-xl border border-hairline-subtle bg-surface p-5 shadow-sm md:p-7";

  const waButton = waHref ? (
    <div className="mt-6">
      <a href={waHref} target="_blank" rel="noreferrer" className={primaryBtn}>
        <MessageCircle className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        {awaitingTime ? "إرسال طلب الحجز عبر واتساب" : "تأكيد الحجز عبر واتساب"}
      </a>
      <p className="mt-2 text-center text-[14px] leading-relaxed text-brown-500">
        إرسال الرسالة هو اللي بيثبّت الموعد مع الفريق.
      </p>
    </div>
  ) : null;

  const summary = (
    <div className="mt-5 rounded-ds-lg bg-cream-100 p-5">
      <p className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-display text-[17px] font-bold text-brown-700">{selectionLine}</span>
        {svc && (
          <span className="font-display text-[23px] font-extrabold text-amber-800" dir="ltr">
            {formatUsdShort(svc.price_usd)}
          </span>
        )}
      </p>
      <p className="mt-1 text-[14px] text-brown-500">
        رقم الطلب: <span dir="ltr">{ref}</span>
      </p>
      {b.scheduled_at && (
        <div className="mt-3 border-t border-hairline-subtle pt-3">
          <p className="text-[13px] text-brown-500">الموعد</p>
          <p className="mt-0.5 font-display text-[17px] font-bold text-brown-700">
            {new Intl.DateTimeFormat("ar-u-nu-latn", { timeZone: "Asia/Damascus", weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", hour12: true }).format(new Date(b.scheduled_at))}
            <span className="ms-2 text-[13px] font-bold text-brown-500">بتوقيت دمشق</span>
          </p>
          {(() => {
            let localTz = "UTC";
            try { localTz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
            if (localTz === "Asia/Damascus") return null;
            const local = new Intl.DateTimeFormat("ar-u-nu-latn", { hour: "numeric", minute: "2-digit", hour12: true, weekday: "long", day: "numeric", month: "long" }).format(new Date(b.scheduled_at!));
            return <p className="mt-1 text-[13px] text-brown-500">بتوقيتك المحلي: {local} ({localTz})</p>;
          })()}
        </div>
      )}
    </div>
  );

  if (waiting) {
    return (
      <div className="mx-auto max-w-[44rem] px-5 py-10 md:py-14">
        <span className="grid h-16 w-16 place-items-center rounded-ds-lg bg-amber-100 text-amber-800">
          <Hourglass className="h-8 w-8" strokeWidth={1.75} aria-hidden />
        </span>
        <h1 className="mt-5 font-display text-[42px] font-extrabold leading-tight text-brown-700">
          وصلنا <span className="mark">طلبك</span>.
        </h1>
        <p className="mt-4 inline-flex rounded-ds-pill bg-amber-100 px-4 py-1.5 font-display text-[14px] font-bold text-amber-800">
          وصلنا إيصال الدفع · بانتظار التأكيد
        </p>
        <p className="mt-4 text-[17px] leading-[1.85] text-brown-700">
          عم نراجع الإيصال يدوياً — هذا طبيعي، وهيك بنتأكّد إن كل شي صح. أول ما يتأكّد، بنرجعلك على واتساب لنتفق على اليوم والساعة.
        </p>
        <dl className="mt-6 divide-y divide-hairline-subtle rounded-ds-lg bg-cream-100 px-5">
          <div className="flex justify-between gap-3 py-3">
            <dt className="text-[14px] text-brown-500">الجلسة</dt>
            <dd className="font-display text-[15px] font-bold text-brown-700">{selectionLine}</dd>
          </div>
          <div className="flex justify-between gap-3 py-3">
            <dt className="text-[14px] text-brown-500">المبلغ</dt>
            <dd className="font-display text-[15px] font-bold text-brown-700">
              {svc && <span dir="ltr">{formatUsdShort(svc.price_usd)}</span>} · {paymentLabel}
            </dd>
          </div>
          <div className="flex justify-between gap-3 py-3">
            <dt className="text-[14px] text-brown-500">رقم الطلب</dt>
            <dd className="font-display text-[15px] font-bold text-brown-700" dir="ltr">{ref}</dd>
          </div>
        </dl>
        <h2 className="mt-10 font-display text-[23px] font-extrabold text-brown-700">شو بيصير هلّق؟</h2>
        <ol className="mt-4 divide-y divide-hairline-subtle">
          <li className="flex items-center gap-4 py-4">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-ds-md bg-green-100 text-green-800">
              <Check className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            </span>
            <span className="text-[17px] text-brown-700">الطلب والإيصال وصلوا.</span>
          </li>
          <li className="flex items-center gap-4 py-4">
            <span className="w-10 flex-none text-center font-display text-[26px] font-extrabold text-amber-800">02</span>
            <span className="text-[17px] text-brown-700">
              المراجعة اليدوية للإيصال <span className="text-brown-500">هلّق.</span>
            </span>
          </li>
          <li className="flex items-center gap-4 py-4">
            <span className="w-10 flex-none text-center font-display text-[26px] font-extrabold text-amber-800">03</span>
            <span className="text-[17px] text-brown-700">الاتفاق على اليوم والساعة على واتساب، بتوقيتك.</span>
          </li>
        </ol>
        <HelpCard title="في سؤال أو تعديل؟" href={helpWa} />
      </div>
    );
  }

  const headline =
    status === "verified"
      ? "تأكّد الدفع."
      : needsReceipt || (mode === "at_clinic" && awaitingTime)
        ? "الدفع، وبعدها الموعد"
        : awaitingTime
          ? "وصلنا طلبك."
          : "تثبّت الموعد.";

  return (
    <div className="mx-auto max-w-[44rem] px-5 py-8 md:py-12">
      {showIndicator && (
        <div className="mb-6">
          <StepIndicator steps={BOOKING_STEPS} current={3} />
        </div>
      )}
      <h1 className="font-display text-[34px] font-extrabold leading-tight text-brown-700">{headline}</h1>
      {summary}

      {mode === "receipt" && status === "unpaid" && rejection && (
        <div className="mt-6 rounded-ds-lg bg-coral-100 p-5">
          <p className="flex items-center gap-2 font-display text-[17px] font-bold text-coral-700">
            <AlertCircle className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            ما قدرنا نعتمد الإيصال
          </p>
          <p className="mt-1 text-[15px] leading-relaxed text-brown-700">{rejection}</p>
          <p className="mt-2 text-[14px] text-brown-500">فيكم ترفعوا إيصال جديد تحت، أو تبعتوه على واتساب.</p>
        </div>
      )}

      {needsReceipt && (
        <div className="mt-6">
          <BookingPaymentPanel
            priceUsd={Number(svc?.price_usd ?? 0)}
            serviceTitle={svc?.title_ar ?? null}
            region={paymentRegion ?? bookingPaymentRegionFor(b.payment_method)}
            onRegionChange={setPaymentRegion}
            instructions={pays as any}
            {...(status === "unpaid" ? { onUpload: uploadFile } : {})}
          />
          {status === "unpaid" && receiptWaHref && (
            <p className="mt-4 text-[14px] text-brown-500">
              أو{" "}
              <a href={receiptWaHref} target="_blank" rel="noreferrer" className="font-bold text-amber-800 underline underline-offset-4">
                إرسال الإيصال عبر واتساب
              </a>
              .
            </p>
          )}
        </div>
      )}

      {mode === "at_clinic" && (
        <section className={`mt-6 ${card}`}>
          <p className="flex items-center gap-2 font-display text-[19px] font-extrabold text-brown-700">
            <MapPin className="h-5 w-5 text-amber-800" strokeWidth={1.75} aria-hidden />
            الدفع كاش بالعيادة يوم الموعد.
          </p>
          <p className="mt-1 text-[15px] text-brown-500">مكان الجلسة: {clinicAddress}</p>
          {payInstruction && (
            <>
              <h2 className="mt-4 font-display text-[17px] font-bold text-brown-700">{payInstruction.title_ar}</h2>
              <div
                className="payment-md mt-2 text-[15px] leading-relaxed text-brown-500 [&_a]:text-amber-800 [&_strong]:text-brown-700 [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:ps-5 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:ps-5"
                dangerouslySetInnerHTML={{ __html: renderPaymentInstructions(payInstruction.body_md ?? "") }}
              />
            </>
          )}
        </section>
      )}

      {!awaitingTime && mode === "card" && status === "unpaid" && (
        <div className="mt-6 rounded-ds-lg bg-amber-100 p-5">
          <p className="flex items-center gap-2 font-display text-[17px] font-bold text-brown-700">
            <CreditCard className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            ما اكتمل الدفع
          </p>
          <p className="mt-1 text-[15px] text-brown-500">الحجز محفوظ. بنبعت تفاصيل الدفع على واتساب.</p>
        </div>
      )}

      {status === "verified" && (
        <div className="mt-6 rounded-ds-lg bg-green-100 p-5">
          <p className="font-display text-[17px] font-bold text-green-800">تأكّد الدفع.</p>
          <p className="mt-1 text-[15px] text-green-800">بنتواصل على واتساب لتحديد موعد الجلسة.</p>
          <Link to="/portal" search={{ tab: "bookings" }} className="mt-4 inline-flex min-h-11 items-center rounded-ds-pill bg-surface px-5 font-display text-sm font-bold text-brown-700">
            فتح لوحتي
          </Link>
        </div>
      )}

      {waButton}

      <HelpCard title="في سؤال أو تعديل؟" href={helpWa} />
    </div>
  );
}
